import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import LocalActivityOutlinedIcon from "@mui/icons-material/LocalActivityOutlined";
import { alpha } from "@mui/material/styles";

import { DateTime } from "luxon";

// Booking dates are fake UTC — the club's wall clock labelled Z — so they are
// read back in that frame, never the browser's.
const formatHorizon = (iso) => (iso ? DateTime.fromISO(iso, { zone: "utc" }).toFormat("d LLL yyyy") : "");

// The same date without the year, for the opening half of a range that already
// carries it: "1 Oct – 30 Nov 2026".
const formatHorizonShort = (iso) => (iso ? DateTime.fromISO(iso, { zone: "utc" }).toFormat("d LLL") : "");

/**
 * What the client's own packages let them reach, and the way to get there.
 *
 * A pack outlives the club's booking window, so the classes it covers stay
 * bookable long after the calendar would otherwise stop — but nothing on the
 * page says so, and a client who has hit the end of the list once won't try
 * again. As a run of prose it read like a disclaimer; contained, with the jump
 * as a real button, it reads as the tool it is.
 *
 * Warning-toned rather than the page's green: everything else in that colour is
 * something to book, and this is the one thing on the page that is a condition
 * on booking — dates, limits, and what the credit will not pay for.
 */
function PackHorizonBanner({ horizon, start, count = 1, capped = false, noun: covers = "classes", onJump, sx }) {
  if (!horizon) return null;

  const plural = count > 1;
  const noun = plural ? "packages" : "package";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.warning.main, 0.45),
        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
        ...sx
      }}
    >
      <LocalActivityOutlinedIcon sx={{ color: "warning.dark", fontSize: "1.25rem", flexShrink: 0 }} />

      <Box sx={{ flex: 1, minWidth: "10rem" }}>
        <Typography sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" }, fontWeight: 700, color: "warning.dark", lineHeight: 1.35 }}>
          {/* A pack with no end date has no date to quote — say the reach, not a
              deadline it does not have. Same for one running years out: the
              ceiling is a year, and naming its real expiry would promise dates
              the calendar will not open. */}
          {capped
            ? `Your ${noun} ${plural ? "cover" : "covers"} ${covers} up to a year ahead`
            : start
              ? `Your ${noun} ${plural ? "run" : "runs"} ${formatHorizonShort(start)} – ${formatHorizon(horizon)}`
              : `Your ${noun} ${plural ? "cover" : "covers"} ${covers} to ${formatHorizon(horizon)}`}
        </Typography>
        <Typography sx={{ fontSize: { xs: "0.6875rem", sm: "0.75rem" }, color: "text.secondary", lineHeight: 1.35 }}>
          {start ? "Sessions before it starts can't be paid with it." : "You can book that far ahead."}
        </Typography>
      </Box>

      {/* Only while the credit has yet to open — once it has, the only place a
          jump could go is backwards, into weeks it can no longer pay for. */}
      {start && (
        <Button size="small" variant="outlined" color="warning" onClick={onJump} sx={{ textTransform: "none", flexShrink: 0, fontWeight: 700 }}>
          Jump to {formatHorizonShort(start)}
        </Button>
      )}
    </Box>
  );
}

export default PackHorizonBanner;
